import { patchExif, PatchMetadata } from './exifUtils';

type WritableDirectory = FileSystemDirectoryHandle;
type WritableFile = FileSystemFileHandle;

export type DirectWriteTarget = {
  id: string;
  downloadUrl: string;
  blobPath?: string;
  targetRelativePath: string;
  originalRelativePath?: string | null;
  sourceHandle?: FileSystemFileHandle;
  size?: number;
};

export type DirectWriteResult = {
  id: string;
  targetRelativePath: string;
  originalRelativePath?: string | null;
  status: "written" | "failed" | "skipped";
  method?: "patched" | "downloaded" | "save-picker";
  bytesWritten?: number;
  renamed?: boolean;
  deletedOriginal?: boolean;
  error?: string;
};

type ProgressCallback = (processed: number, total: number, current: DirectWriteResult) => void;

function normalizeParts(path: string | null | undefined, dropRoot?: string | null): string[] {
  if (!path) return [];
  const trimmed = path.replace(/^\/+/, "");
  const parts = trimmed.split("/").filter(Boolean);
  if (dropRoot && parts.length && parts[0].toLowerCase() === dropRoot.toLowerCase()) {
    parts.shift();
  }
  return parts;
}

async function resolveDirectory(
  root: WritableDirectory,
  parts: string[],
  create: boolean
): Promise<WritableDirectory> {
  let current = root;
  for (const part of parts) {
    current = await current.getDirectoryHandle(part, { create });
  }
  return current;
}

async function getExistingFileHandle(
  root: WritableDirectory,
  parts: string[]
): Promise<WritableFile | null> {
  if (!parts.length) return null;
  try {
    const dir = parts.length > 1 ? await resolveDirectory(root, parts.slice(0, -1), false) : root;
    return await dir.getFileHandle(parts[parts.length - 1], { create: false });
  } catch {
    console.warn("[DirectWrite][Telemetry] rename-not-supported-or-missing-file", parts.join("/"));
    return null;
  }
}

async function deleteFileIfExists(root: WritableDirectory, parts: string[]): Promise<boolean> {
  if (!parts.length) return false;
  try {
    const dir = parts.length > 1 ? await resolveDirectory(root, parts.slice(0, -1), false) : root;
    await dir.removeEntry(parts[parts.length - 1], { recursive: false });
    return true;
  } catch {
    return false;
  }
}

function isReadOnlyError(err: any): boolean {
  const message = (err?.message || "").toLowerCase();
  return message.includes("read-only") || message.includes("readonly");
}

async function ensureFileWritePermission(handle: WritableFile): Promise<PermissionState | null> {
  const query = (handle as any).queryPermission as (opts?: FileSystemPermissionDescriptor) => Promise<PermissionState> | undefined;
  const request = (handle as any).requestPermission as (opts?: FileSystemPermissionDescriptor) => Promise<PermissionState> | undefined;

  if (typeof query === "function") {
    try {
      const state = await query.call(handle, { mode: "readwrite" });
      if (state === "granted") return state;
    } catch {
      // ignore query errors
    }
  }

  if (typeof request === "function") {
    try {
      return (await request.call(handle, { mode: "readwrite" })) ?? null;
    } catch {
      return null;
    }
  }

  return null;
}

async function createWritableSafe(
  handle: WritableFile,
  options: { keepExistingData: boolean }
): Promise<FileSystemWritableFileStream> {
  try {
    return await handle.createWritable(options);
  } catch (err: any) {
    const message = (err?.message || "").toLowerCase();
    if (message.includes("read-only") || message.includes("permission") || message.includes("not allowed")) {
      const perm = await ensureFileWritePermission(handle);
      if (perm === "granted") {
        return await handle.createWritable(options);
      }
    }
    throw err;
  }
}

async function streamToFile(
  fileHandle: WritableFile,
  downloadUrl: string,
  chunkSize?: number
): Promise<number> {
  const response = await fetch(downloadUrl, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status} ${response.statusText}`);
  }

  const writable = await createWritableSafe(fileHandle, { keepExistingData: true });
  let total = 0;

  try {
    if (typeof (writable as any).seek === "function") {
      await (writable as any).seek(0);
    }
    if (response.body && typeof response.body.getReader === "function") {
      const reader = response.body.getReader();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        if (chunkSize && value.byteLength > chunkSize) {
          for (let offset = 0; offset < value.byteLength; offset += chunkSize) {
            const slice = value.subarray(offset, Math.min(value.byteLength, offset + chunkSize));
            await writable.write(slice);
            total += slice.byteLength;
          }
        } else {
          await writable.write(value);
          total += value.byteLength;
        }
      }
    } else {
      const buffer = await response.arrayBuffer();
      const data = new Uint8Array(buffer);
      if (data.byteLength) {
        if (chunkSize && data.byteLength > chunkSize) {
          for (let offset = 0; offset < data.byteLength; offset += chunkSize) {
            const slice = data.subarray(offset, Math.min(data.byteLength, offset + chunkSize));
            await writable.write(slice);
            total += slice.byteLength;
          }
        } else {
          await writable.write(data);
          total += data.byteLength;
        }
      }
    }
    if (total === 0) {
      throw new Error("Download failed: empty body");
    }
    await writable.truncate(total);
    await writable.close();
  } catch (err) {
    try {
      await writable.abort();
    } catch {
      // ignore abort errors
    }
    throw err;
  }

  return total;
}

// Write a Blob directly (for patched files)
async function writeBlob(fileHandle: WritableFile, blob: Blob): Promise<number> {
  const writable = await createWritableSafe(fileHandle, { keepExistingData: false });
  try {
    await writable.write(blob);
    await writable.close();
    return blob.size;
  } catch (err) {
    try { await writable.abort(); } catch { }
    throw err;
  }
}

async function streamToFileWithRetry(
  rootHandle: WritableDirectory,
  resolvedDir: WritableDirectory,
  targetParts: string[],
  targetName: string,
  fileHandle: WritableFile,
  downloadUrl: string,
  chunkSize?: number
): Promise<{ bytes: number; handle: WritableFile }> {
  try {
    const bytes = await streamToFile(fileHandle, downloadUrl, chunkSize);
    return { bytes, handle: fileHandle };
  } catch (err) {
    if (!isReadOnlyError(err)) throw err;
    await deleteFileIfExists(rootHandle, targetParts);
    const retryHandle = await resolvedDir.getFileHandle(targetName, { create: true });
    const bytes = await streamToFile(retryHandle, downloadUrl, chunkSize);
    return { bytes, handle: retryHandle };
  }
}

async function writeBlobWithRetry(
  rootHandle: WritableDirectory,
  resolvedDir: WritableDirectory,
  targetParts: string[],
  targetName: string,
  fileHandle: WritableFile,
  blob: Blob
): Promise<{ bytes: number; handle: WritableFile }> {
  try {
    const bytes = await writeBlob(fileHandle, blob);
    return { bytes, handle: fileHandle };
  } catch (err) {
    if (!isReadOnlyError(err)) throw err;
    await deleteFileIfExists(rootHandle, targetParts);
    const retryHandle = await resolvedDir.getFileHandle(targetName, { create: true });
    const bytes = await writeBlob(retryHandle, blob);
    return { bytes, handle: retryHandle };
  }
}

export async function writeDirectToFileSystem(
  rootHandle: WritableDirectory,
  targets: DirectWriteTarget[],
  options?: {
    dropRootSegment?: string | null;
    preferMove?: boolean;
    chunkSize?: number;
    onProgress?: ProgressCallback;
    ensurePermission?: () => Promise<PermissionState>;
    resolveDownloadUrl?: (target: DirectWriteTarget) => Promise<string | null>;
    filesStatus?: Record<string, any>; // Pass backend status for metadata
  }
): Promise<DirectWriteResult[]> {
  const total = targets.length;
  const results: DirectWriteResult[] = [];
  const dropRoot = options?.dropRootSegment ?? null;
  const preferMove = options?.preferMove ?? false;
  const chunkSize = options?.chunkSize;
  const onProgress = options?.onProgress;
  const ensurePermission = options?.ensurePermission;
  const resolveDownloadUrl = options?.resolveDownloadUrl;
  const filesStatus = options?.filesStatus || {};

  for (let idx = 0; idx < targets.length; idx++) {
    const target = targets[idx];
    const result: DirectWriteResult = {
      id: target.id,
      targetRelativePath: target.targetRelativePath,
      originalRelativePath: target.originalRelativePath ?? undefined,
      status: "failed",
    };

    let targetParts: string[] = [];
    let originalParts: string[] = [];
    let createdTarget = false;
    let pendingMove = false;
    let originalHandle: WritableFile | null = null;
    let patchedSuccess = false;

    try {
      if (ensurePermission) {
        const perm = await ensurePermission();
        if (perm !== "granted") {
          console.warn("[DirectWrite][Telemetry] permission-denied");
          throw new Error("Write permission not granted");
        }
      }

      targetParts = normalizeParts(target.targetRelativePath, dropRoot);
      if (!targetParts.length) {
        throw new Error("Missing target path");
      }
      const targetName = targetParts[targetParts.length - 1];
      const targetDir = targetParts.length > 1 ? targetParts.slice(0, -1) : [];
      const resolvedDir = await resolveDirectory(rootHandle, targetDir, true);

      originalParts = normalizeParts(target.originalRelativePath, dropRoot);
      if (!originalParts.length) {
        originalParts = [...targetParts];
      }
      const needsRename =
        originalParts.length > 0 &&
        originalParts.join("/") !== targetParts.join("/");

      let fileHandle: WritableFile | null = null;
      let deletedOriginal = false;

      // Try to get original handle first
      if (target.sourceHandle) {
        originalHandle = target.sourceHandle;
      } else if (originalParts.length > 0) {
        originalHandle = await getExistingFileHandle(rootHandle, originalParts);
      }

      // --- HYBRID PATCHING LOGIC BEGIN ---
      // Attempt to patch locally if we have the original handle and metadata
      const fileId = target.id; // Usually filename
      // The fileID in targets might be the safe_fname, let's try to match it with status
      // We'll trust the caller to pass a map that works, or we find by iterating.
      // Actually targets[idx].id should match keys in filesStatus if set up correctly.

      const statusEntry = filesStatus[target.id];

      let patchAttempted = false;

      if (originalHandle && statusEntry) {
        const state = statusEntry.processing_state;

        // Robust extraction of lat/lng
        let lat: number | undefined;
        let lng: number | undefined;
        let date: string | undefined;

        // Candidate objects to search for coordinates
        const candidates = [
          state?.marker,
          state?.marker?.final_location, // If object
          state?.marker?.finalLocation, // If object
          state?.final_location, // If object
          state
        ];

        for (const c of candidates) {
          if (!c) continue;
          // Check lat
          if (typeof c.lat === 'number') lat = c.lat;
          else if (typeof c.latitude === 'number') lat = c.latitude;

          // Check lng 
          if (typeof c.lng === 'number') lng = c.lng;
          else if (typeof c.longitude === 'number') lng = c.longitude;

          // Check date
          if (typeof c.final_date === 'string') date = c.final_date;
          else if (typeof c.finalDate === 'string') date = c.finalDate;
          else if (typeof c.date === 'string') date = c.date;

          // Date object case { date: "..." }
          if (c.final_date && typeof c.final_date.date === 'string') date = c.final_date.date;

          // Break if we found both lat/lng and date, or just keep looking for best match?
          // Usually one source has it all.
          if (typeof lat === 'number' && typeof lng === 'number' && date) break;
        }

        const meta: PatchMetadata = {};
        if (typeof lat === 'number' && typeof lng === 'number') {
          meta.lat = lat;
          meta.lng = lng;
        }
        if (date) {
          meta.date = date;
        }

        if (meta.lat !== undefined || meta.date) {
          patchAttempted = true;
          try {
            const originalFile = await originalHandle.getFile();
            // console.debug(`[DirectWrite] Patching ${targetName}`, meta);
            const modifiedBlob = await patchExif(originalFile, meta);

            // Write to target
            // If needs rename, we write to new target. 
            // If same name, we overwrite originalHandle.

            if (needsRename) {
              // We need a handle for the target
              if (!fileHandle) {
                try {
                  fileHandle = await resolvedDir.getFileHandle(targetName, { create: false });
                } catch {
                  fileHandle = await resolvedDir.getFileHandle(targetName, { create: true });
                  createdTarget = true;
                }
              }
              const writeRes = await writeBlobWithRetry(rootHandle, resolvedDir, targetParts, targetName, fileHandle, modifiedBlob);
              fileHandle = writeRes.handle;
            } else {
              // Overwrite original
              fileHandle = originalHandle;
              const writeRes = await writeBlobWithRetry(rootHandle, resolvedDir, targetParts, targetName, fileHandle, modifiedBlob);
              fileHandle = writeRes.handle;
            }

            result.bytesWritten = modifiedBlob.size;
            patchedSuccess = true;
            result.method = "patched";
            console.info(`[DirectWrite] Client-side patch success: ${targetName}`);

          } catch (patchErr) {
            console.warn(`[DirectWrite] Patch failed for ${targetName}, falling back to download.`, patchErr);
            // Fallback will happen below
            patchedSuccess = false;
            // Ensure we don't reuse a potentially read-only handle from the patch attempt.
            fileHandle = null;
          }
        } else {
          if (statusEntry) {
            // Low-level debug only if really needed
            // console.debug(`[DirectWrite] No metadata found in statusEntry`, statusEntry);
          }
        }
      }
      // --- HYBRID PATCHING LOGIC END ---

      if (!patchedSuccess) {
        // Standard Download Logic
        if (needsRename && preferMove && !fileHandle) {
          // ... existing move logic ...
          // We only use move if we are NOT creating a new file from stream
          // Wait, if we stream, we are RE-WRITING content. 'move' is only for renaming without content change.
          // DirectWrite ALWAYS changes content (metadata). So 'move' on originalHandle is useless if we assume we must write new bytes.
          // EXCEPT: if the browser supports 'move', we might move then write?
          // Actually, the original 'move' logic in this file was:
          // 1. streamToFile (write to target)
          // 2. delete original
          // OR
          // 1. move original to target
          // 2. streamToFile (overwrite target)

          // Let's stick to: Ensure target handle -> Stream to it.
          const moveFn = (originalHandle as any)?.move;
          // If we can move, we move original to target location first to preserve permissions/creation?
          // Actually, streaming overwrites.
          if (originalHandle && typeof moveFn === "function") {
            pendingMove = true;
            fileHandle = originalHandle; // We will write to this moved handle
          }
        }

        if (!fileHandle) {
          try {
            fileHandle = await resolvedDir.getFileHandle(targetName, { create: false });
          } catch {
            fileHandle = await resolvedDir.getFileHandle(targetName, { create: true });
            createdTarget = true;
          }
        }
        // If output equals original path, we will overwrite via originalHandle (set above).

        const resolvedUrl = resolveDownloadUrl ? await resolveDownloadUrl(target) : null;
        const downloadUrl = resolvedUrl || target.downloadUrl;
        if (!downloadUrl) {
          throw new Error("Missing download URL");
        }
        const writeRes = await streamToFileWithRetry(rootHandle, resolvedDir, targetParts, targetName, fileHandle, downloadUrl, chunkSize);
        result.bytesWritten = writeRes.bytes;
        fileHandle = writeRes.handle;
        result.method = "downloaded";
      }

      // Cleanup / Rename logic (Common for both paths)
      if (pendingMove && originalHandle && !patchedSuccess) {
        // Only execute move if we didn't purely patch-write to a new location.
        // If we patched successfully to a new location, we just delete original later.
        // If we are here (download path), and we have pendingMove:
        const moveFn = (originalHandle as any)?.move;
        if (typeof moveFn === "function") {
          try {
            await moveFn.call(originalHandle, resolvedDir, targetName);
            deletedOriginal = true;
          } catch {
            // ignore
          }
        }
      }

      if (!deletedOriginal && needsRename && originalParts.length && !pendingMove) {
        deletedOriginal = await deleteFileIfExists(rootHandle, originalParts);
      }
      result.deletedOriginal = deletedOriginal;
      result.renamed = needsRename && (!pendingMove || deletedOriginal);
      result.status = "written";
    } catch (err: any) {
      if (createdTarget && targetParts.length) {
        await deleteFileIfExists(rootHandle, targetParts);
      }
      result.error = err?.message || String(err);
    }

    results.push(result);
    if (onProgress) {
      onProgress(idx + 1, total, result);
    }
  }

  console.info("[DirectWrite] Write report", results);
  return results;
}
